import { useState, useEffect, useRef } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { Home, Search, Bug, Menu, Settings } from 'lucide-react';
import { isSuggestedTheme, themeLabel } from './themeHint';
import './MobileNav.css';

type Theme = 'dark' | 'light' | 'hc-dark' | 'hc-light' | 'blue';
type FontSize = 'md' | 'lg' | 'xl';

const THEMES: { id: Theme; swatch: string; border: string; label: string }[] = [
  { id: 'dark',     swatch: '#3d1f6e', border: '#c9a227', label: 'Dark' },
  { id: 'light',    swatch: '#e5dece', border: '#7a5c10', label: 'Light' },
  { id: 'hc-dark',  swatch: '#111111', border: '#e0e0e0', label: 'B&W Dark' },
  { id: 'hc-light', swatch: '#eeeeee', border: '#111111', label: 'B&W Light' },
  { id: 'blue',     swatch: '#0b1a30', border: '#4fd8ff', label: 'Blue' },
];

const FONT_SIZES: { id: FontSize; label: string; size: string }[] = [
  { id: 'md', label: 'A', size: '13px' },
  { id: 'lg', label: 'A', size: '15px' },
  { id: 'xl', label: 'A', size: '17px' },
];

interface MobileNavProps {
  onOpenSidebar: () => void;
  theme: Theme;
  onSetTheme: (t: Theme) => void;
  fontSize: FontSize;
  onSetFontSize: (s: FontSize) => void;
}

export default function MobileNav({ onOpenSidebar, theme, onSetTheme, fontSize, onSetFontSize }: MobileNavProps) {
  const location = useLocation();
  const [settingsOpen, setSettingsOpen] = useState(false);
  const settingsRef = useRef<HTMLDivElement>(null);

  const openSearch = () => {
    setSettingsOpen(false);
    window.dispatchEvent(new CustomEvent('mobile-search-open'));
  };

  const openFeedback = () => {
    setSettingsOpen(false);
    window.dispatchEvent(new CustomEvent('open-feedback'));
  };

  useEffect(() => {
    if (!settingsOpen) return;
    const onOutside = (e: MouseEvent) => {
      if (settingsRef.current && !settingsRef.current.contains(e.target as Node)) {
        setSettingsOpen(false);
      }
    };
    document.addEventListener('mousedown', onOutside);
    return () => document.removeEventListener('mousedown', onOutside);
  }, [settingsOpen]);

  return (
    <nav className="mobile-nav" aria-label="Mobile navigation">
      <Link
        to="/"
        className={`mobile-nav__item${location.pathname === '/' ? ' mobile-nav__item--active' : ''}`}
        aria-label="Home"
      >
        <Home size={22} />
        <span>Home</span>
      </Link>

      <button className="mobile-nav__item" onClick={openSearch} aria-label="Search">
        <Search size={22} />
        <span>Search</span>
      </button>

      <button className="mobile-nav__item" onClick={onOpenSidebar} aria-label="Contents">
        <Menu size={22} />
        <span>Contents</span>
      </button>

      <button className="mobile-nav__item" onClick={openFeedback} aria-label="Feedback">
        <Bug size={22} />
        <span>Feedback</span>
      </button>

      {/* Settings — expands upward */}
      <div ref={settingsRef} className="mobile-nav__settings-wrap">
        {settingsOpen && (
          <div className="mobile-nav__settings-popover">
            <div className="mobile-nav__settings-section">
              <span className="mobile-nav__settings-label">Text size</span>
              <div className="mobile-nav__font-btns">
                {FONT_SIZES.map((fs) => (
                  <button
                    key={fs.id}
                    className={`mobile-nav__font-btn${fontSize === fs.id ? ' active' : ''}`}
                    style={{ fontSize: fs.size }}
                    onClick={() => onSetFontSize(fs.id)}
                    aria-label={fs.label}
                  >
                    {fs.label}
                  </button>
                ))}
              </div>
            </div>
            <div className="mobile-nav__settings-section">
              <span className="mobile-nav__settings-label">Theme</span>
              <div className="mobile-nav__theme-swatches">
                {THEMES.map((t) => (
                  <button
                    key={t.id}
                    className={`mobile-nav__theme-swatch${theme === t.id ? ' active' : ''}${isSuggestedTheme(t.id, theme, location.pathname) ? ' mobile-nav__theme-swatch--suggested' : ''}`}
                    style={{ background: t.swatch, borderColor: t.border }}
                    onClick={() => onSetTheme(t.id)}
                    aria-label={themeLabel(t, theme, location.pathname)}
                    title={themeLabel(t, theme, location.pathname)}
                  />
                ))}
              </div>
            </div>
          </div>
        )}
        <button
          className={`mobile-nav__item${settingsOpen ? ' mobile-nav__item--active' : ''}`}
          onClick={() => setSettingsOpen((o) => !o)}
          aria-label="Settings"
        >
          <Settings size={22} />
          <span>Settings</span>
        </button>
      </div>
    </nav>
  );
}
